import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCpu , FiX, FiSend, FiMinus } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import logo from '../assets/logo.png';

const OPENROUTER_API_KEY = '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';
const MODEL = 'mistralai/mixtral-8x7b-instruct';

const SYSTEM_PROMPT = `You are SPE Assistant — the friendly, knowledgeable customer service chatbot for Shree Parasnath Enterprises (SPE).

COMPANY OVERVIEW:
- Name: Shree Parasnath Enterprises (SPE)
- Type: GST Registered Business — Premium Disposable Products Supplier
- GST Number: (GST Registered — customers can request GST invoice)
- Location: India
- Phone: +91 98219 91932
- Email: info.shreeparasnathenterprises@gmail.com
- WhatsApp: +91 98219 91932

PRODUCTS:
1. Mini Shot Cup — 25ml, 50ml | Black & White variants | For tastings, shots, samples
2. Standard Beverage Cup — 100ml, 150ml | Black & White | Offices, events
3. Medium Serving Cup — 200ml, 250ml | Black & White | Cold drinks, juices, desserts
4. Large Party Cup — 350ml, 500ml | Black & White | Parties, canteens, food stalls
5. Jumbo Container — 750ml, 1000ml | Black & White | Catering, meal boxes, bulk food
6. Economy Bulk Pack — All sizes available | Wholesale pricing for distributors

KEY INFO:
- All products available in Black & White color variants
- Sizes: 25ml, 50ml, 100ml, 150ml, 200ml, 250ml, 350ml, 500ml, 750ml, 1000ml
- Bulk wholesale pricing available — contact via WhatsApp
- GST Invoice: Yes, provided for all orders (GST Registered Business)
- MOQ: Flexible — contact for details
- Delivery: Pan India
- Future services: T-Shirt Printing & Brand Merchandise (Coming Soon)
- Payment: Contact for payment terms
- Quality: Food-safe, hygienic, single-use, leak-proof

BEHAVIOR:
- Be warm, professional, and concise — 1-3 sentences max per response
- Always try to help with product queries, pricing, orders, GST, delivery
- For bulk quotes, always direct to WhatsApp: +91 98219 91932
- For GST invoice requests, confirm SPE is GST registered and they'll receive invoice
- If you don't know exact pricing, say "Contact us on WhatsApp for the best bulk pricing"
- Never make up GST numbers — say "Please contact us for the exact GST details"
- Respond in the same language as the user (Hindi or English)
- End responses with a helpful next step when possible`;

const QUICK_REPLIES = [
  'What products do you have?',
  'Do you give bulk discount?',
  'Are you GST registered?',
  'What sizes are available?',
  'How to order?',
  'Delivery info?',
];

function TypingDots() {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:4, padding:'12px 16px' }}>
      {[0,1,2].map(i => (
        <motion.div key={i}
          animate={{ y:[0,-5,0], opacity:[0.4,1,0.4] }}
          transition={{ duration:0.8, repeat:Infinity, delay:i*0.18 }}
          style={{ width:7, height:7, borderRadius:'50%', background:'var(--grad)' }} />
      ))}
    </div>
  );
}

export default function Chatbot() {
  const [open, setOpen]       = useState(false);
  const [msgs, setMsgs]       = useState([
    { role:'assistant', text:'👋 Hi! I\'m SPE Assistant. I can help you with products, pricing, GST invoices, bulk orders, and more. What can I help you with today?', id:0 }
  ]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [unread, setUnread]   = useState(1);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [msgs, loading]);
  useEffect(() => { if (open) { setUnread(0); inputRef.current?.focus(); } }, [open]);

  const send = async (text) => {
    const q = text || input.trim();
    if (!q || loading) return;
    setInput('');

    const userMsg = { role:'user', text:q, id:Date.now() };
    setMsgs(m => [...m, userMsg]);
    setLoading(true);

    try {
      const history = msgs.map(m => ({ role:m.role, content:m.text }));
      const res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method:'POST',
        headers:{
          'Authorization':`Bearer ${OPENROUTER_API_KEY}`,
          'Content-Type':'application/json',
          'HTTP-Referer':'https://shreeparasnathenterprises.com',
          'X-Title':'SPE Assistant',
        },
        body:JSON.stringify({
          model: MODEL,
          temperature: 0.7,
          messages:[
            { role:'system', content:SYSTEM_PROMPT },
            ...history,
            { role:'user', content:q },
          ],
          max_tokens:200,
          temperature:0.7,
        }),
      });
      const data = await res.json();
      const reply = data.choices?.[0]?.message?.content || "Sorry, I couldn't get a response. Please contact us on WhatsApp: +91 98219 91932";
      setMsgs(m => [...m, { role:'assistant', text:reply, id:Date.now()+1 }]);
    } catch {
      setMsgs(m => [...m, { role:'assistant', text:"Sorry, something went wrong. Please reach us on WhatsApp: +91 98219 91932 🙏", id:Date.now()+1 }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {/* Floating Chat Button */}
      <motion.button
        whileHover={{ scale:1.12 }} whileTap={{ scale:0.92 }}
        onClick={() => setOpen(o => !o)}
        style={{
          position:'fixed', bottom:'5.5rem', right:'1.5rem', zIndex:998,
          width:56, height:56, borderRadius:'50%', border:'none', cursor:'pointer',
          background:'var(--grad)',
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow:'0 6px 28px rgba(233,30,140,0.5)',
        }}
        title="Chat with SPE Assistant"
      >
        <div style={{ position:'absolute', inset:-4, borderRadius:'50%',
          background:'rgba(233,30,140,0.3)', animation:'pulse-ring 2s ease-out infinite' }} />
        <AnimatePresence mode="wait">
          <motion.div key={open?'x':'c'}
            initial={{ scale:0, rotate:-90 }} animate={{ scale:1, rotate:0 }}
            exit={{ scale:0, rotate:90 }} transition={{ duration:0.22 }}>
            {open ? <FiX size={24} color="#fff" /> : <FiCpu  size={24} color="#fff" />}
          </motion.div>
        </AnimatePresence>
        {unread > 0 && !open && (
          <div style={{ position:'absolute', top:-4, right:-4, width:18, height:18, borderRadius:'50%',
            background:'#FF6B2B', color:'#fff', fontSize:'0.65rem', fontWeight:800,
            display:'flex', alignItems:'center', justifyContent:'center',
            border:'2px solid #fff', fontFamily:'var(--font-body)' }}>
            {unread}
          </div>
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity:0, y:30, scale:0.92 }}
            animate={{ opacity:1, y:0, scale:1 }}
            exit={{ opacity:0, y:20, scale:0.92 }}
            transition={{ duration:0.35, ease:[0.22,1,0.36,1] }}
            style={{
              position:'fixed', bottom:'8rem', right:'1.5rem', zIndex:997,
              width:'clamp(300px,90vw,380px)',
              height:'clamp(420px,60vh,540px)',
              background:'#fff',
              borderRadius:24,
              boxShadow:'0 24px 80px rgba(61,0,102,0.18), 0 4px 20px rgba(233,30,140,0.1)',
              border:'1.5px solid rgba(233,30,140,0.12)',
              display:'flex', flexDirection:'column', overflow:'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              background:'var(--grad)', padding:'1rem 1.2rem',
              display:'flex', alignItems:'center', justifyContent:'space-between',
              flexShrink:0,
            }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.65rem' }}>
                <div style={{ position:'relative' }}>
                  <img src={logo} alt="SPE" style={{ width:38, height:38, borderRadius:'50%', objectFit:'cover', border:'2px solid rgba(255,255,255,0.5)' }} />
                  <div style={{ position:'absolute', bottom:1, right:1, width:9, height:9, borderRadius:'50%',
                    background:'#4ADE80', border:'2px solid #fff' }} />
                </div>
                <div>
                  <p style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:'0.9rem', color:'#fff', lineHeight:1.1 }}>SPE Assistant</p>
                  <p style={{ fontFamily:'var(--font-body)', fontSize:'0.65rem', color:'rgba(255,255,255,0.8)' }}>● Online — Usually replies instantly</p>
                </div>
              </div>
              <div style={{ display:'flex', gap:'0.4rem' }}>
                <button onClick={() => setOpen(false)}
                  style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:8,
                    width:32, height:32, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff' }}>
                  <FiMinus size={16} />
                </button>
                <button onClick={() => setOpen(false)}
                  style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:8,
                    width:32, height:32, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff' }}>
                  <FiX size={16} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex:1, overflowY:'auto', padding:'1rem', display:'flex', flexDirection:'column', gap:'0.7rem',
              background:'linear-gradient(180deg,#fff9ff 0%,#fff 100%)' }}>
              {msgs.map(m => (
                <div key={m.id} className="chat-msg"
                  style={{ display:'flex', flexDirection:m.role==='user'?'row-reverse':'row',
                    alignItems:'flex-end', gap:'0.5rem' }}>
                  {m.role==='assistant' && (
                    <img src={logo} alt="SPE" style={{ width:26, height:26, borderRadius:'50%', objectFit:'cover', flexShrink:0 }} />
                  )}
                  <div className={m.role==='user' ? 'chat-bubble-user' : 'chat-bubble-bot'}
                    style={{ padding:'0.65rem 0.95rem', maxWidth:'80%',
                      fontFamily:'var(--font-body)', fontSize:'0.83rem', lineHeight:1.55,
                      boxShadow: m.role==='user' ? '0 4px 14px rgba(233,30,140,0.3)' : 'var(--shadow-sm)' }}>
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ display:'flex', alignItems:'flex-end', gap:'0.5rem' }}>
                  <img src={logo} alt="SPE" style={{ width:26, height:26, borderRadius:'50%', objectFit:'cover', flexShrink:0 }} />
                  <div className="chat-bubble-bot"><TypingDots /></div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Quick replies */}
            <div style={{ padding:'0.5rem 0.75rem', borderTop:'1px solid rgba(233,30,140,0.08)',
              display:'flex', gap:6, overflowX:'auto', flexShrink:0,
              background:'#fff', scrollbarWidth:'none' }}>
              {QUICK_REPLIES.map(q => (
                <button key={q} onClick={() => send(q)}
                  style={{ whiteSpace:'nowrap', padding:'0.3rem 0.75rem', borderRadius:99, flexShrink:0,
                    background:'linear-gradient(135deg,rgba(233,30,140,0.07),rgba(255,107,43,0.05))',
                    border:'1px solid rgba(233,30,140,0.2)',
                    fontFamily:'var(--font-body)', fontSize:'0.7rem', fontWeight:600,
                    color:'#E91E8C', cursor:'pointer', transition:'all 0.2s' }}
                  onMouseEnter={e => { e.target.style.background='var(--grad)'; e.target.style.color='#fff'; e.target.style.border='none'; }}
                  onMouseLeave={e => { e.target.style.background='linear-gradient(135deg,rgba(233,30,140,0.07),rgba(255,107,43,0.05))'; e.target.style.color='#E91E8C'; e.target.style.border='1px solid rgba(233,30,140,0.2)'; }}
                >{q}</button>
              ))}
            </div>

            {/* Input area */}
            <div style={{ padding:'0.75rem', borderTop:'1px solid rgba(233,30,140,0.08)',
              display:'flex', gap:'0.5rem', flexShrink:0, background:'#fff' }}>
              <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
                placeholder="Type your message…"
                style={{ flex:1, padding:'0.65rem 1rem',
                  background:'#FFF5F9', border:'1.5px solid rgba(233,30,140,0.15)', borderRadius:12,
                  fontFamily:'var(--font-body)', fontSize:'0.83rem', color:'var(--text)',
                  outline:'none', transition:'border 0.2s' }}
                onFocus={e => e.target.style.borderColor='#E91E8C'}
                onBlur={e => e.target.style.borderColor='rgba(233,30,140,0.15)'}
              />
              <motion.button whileHover={{ scale:1.08 }} whileTap={{ scale:0.92 }}
                onClick={() => send()}
                disabled={!input.trim() || loading}
                style={{ width:40, height:40, borderRadius:11, border:'none', cursor:'pointer', flexShrink:0,
                  background: input.trim() && !loading ? 'var(--grad)' : 'rgba(233,30,140,0.15)',
                  color: input.trim() && !loading ? '#fff' : 'rgba(233,30,140,0.4)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  boxShadow: input.trim() ? '0 4px 14px rgba(233,30,140,0.35)' : 'none',
                  transition:'all 0.25s' }}>
                <FiSend size={16} />
              </motion.button>
            </div>

            {/* WhatsApp fallback */}
            <div style={{ padding:'0.5rem 0.75rem 0.75rem', background:'#fff', textAlign:'center' }}>
              <a href="https://wa.me/919821991932?text=Hello SPE! I need help."
                target="_blank" rel="noreferrer"
                style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem',
                  fontFamily:'var(--font-body)', fontSize:'0.68rem', fontWeight:600,
                  color:'#25D366', textDecoration:'none' }}>
                <FaWhatsapp size={13} /> Prefer WhatsApp? Chat directly
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
